/**
 * MusicManager — plays background music tracks with shuffle and auto-advance.
 * Tracks are hosted on Vercel Blob storage.
 *
 * Usage:
 *   const music = new MusicManager()
 *   music.play()   // starts a random track
 *   music.stop()   // stops playback
 *   music.next()   // skip to another random track
 *
 * Controls:
 *   M key = mute/unmute
 *   N key = skip track
 */

const BLOB_BASE = 'https://fvx03iprzptavlb7.public.blob.vercel-storage.com/music'

const ALL_TRACKS: string[] = [
  `${BLOB_BASE}/cipher.mp3`,
  `${BLOB_BASE}/endless-blue-synthwave.mp3`,
  `${BLOB_BASE}/eternal-light-synthwave.mp3`,
  `${BLOB_BASE}/ethernight-club.mp3`,
  `${BLOB_BASE}/motion-blur-synthwave.mp3`,
  `${BLOB_BASE}/neon-drive-dream-sunlight.mp3`,
  `${BLOB_BASE}/neon-glimmer.mp3`,
  `${BLOB_BASE}/neon-laser-horizon.mp3`,
  `${BLOB_BASE}/newer-wave.mp3`,
  `${BLOB_BASE}/noctivagant-synthwave.mp3`,
  `${BLOB_BASE}/retro-future-clean.mp3`,
  `${BLOB_BASE}/voltaic.mp3`,
]

export class MusicManager {
  private audio: HTMLAudioElement
  private queue: string[] = []
  private _volume = 0.4
  private _muted = false
  private playing = false

  constructor() {
    this.audio = new Audio()
    this.audio.volume = this._volume
    this.audio.crossOrigin = 'anonymous'
    this.audio.addEventListener('ended', () => this.onTrackEnd())
    this.audio.addEventListener('error', () => this.onTrackEnd())
  }

  /** Start playing (or resume if paused). */
  play(): void {
    if (ALL_TRACKS.length === 0) return
    this.playing = true
    if (this.audio.src && this.audio.paused) {
      this.audio.play().catch(() => {})
      return
    }
    this.playNext()
  }

  /** Pause playback without resetting position. */
  pause(): void {
    this.playing = false
    this.audio.pause()
  }

  /** Stop playback and reset. */
  stop(): void {
    this.playing = false
    this.audio.pause()
    this.audio.currentTime = 0
    this.audio.removeAttribute('src')
  }

  /** Skip to the next random track. */
  next(): void {
    if (!this.playing) return
    this.playNext()
  }

  /** Volume 0–1. */
  get volume(): number {
    return this._volume
  }

  set volume(v: number) {
    this._volume = Math.max(0, Math.min(1, v))
    this.audio.volume = this._muted ? 0 : this._volume
  }

  /** Toggle mute (preserves volume level). */
  get muted(): boolean {
    return this._muted
  }

  set muted(m: boolean) {
    this._muted = m
    this.audio.volume = m ? 0 : this._volume
  }

  /** Whether music is currently playing. */
  get isPlaying(): boolean {
    return this.playing && !this.audio.paused
  }

  // --- internals ---

  private playNext(): void {
    const track = this.dequeue()
    if (!track) return
    this.audio.src = track
    this.audio.play().catch(() => {})
  }

  private onTrackEnd(): void {
    if (this.playing) {
      this.playNext()
    }
  }

  /** Pull the next track from the shuffled queue, refilling when empty. */
  private dequeue(): string | undefined {
    if (this.queue.length === 0) {
      this.queue = shuffle([...ALL_TRACKS])
    }
    return this.queue.pop()
  }
}

/** Fisher-Yates shuffle. */
function shuffle<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[arr[i], arr[j]] = [arr[j], arr[i]]
  }
  return arr
}
